import React, { useState, useEffect, useCallback } from 'react';
import {
  Lock, ArrowUpRight, Send, FileText, Users, Plus, Eye, Trash2,
  RefreshCw, Mail, Calendar, CheckCircle, XCircle, Sparkles, Clock,
  ChevronDown, AlertCircle,
} from 'lucide-react';
import '../styles/admin-newsletter.css';

const API = process.env.REACT_APP_BACKEND_URL;

/* ====== Password Gate (reuses analytics pattern) ====== */
function PasswordGate({ onUnlock }) {
  const [pw, setPw] = useState('');
  const [error, setError] = useState(false);
  const handleSubmit = (e) => {
    e.preventDefault();
    if (pw.trim()) { setError(false); onUnlock(pw.trim()); }
    else { setError(true); }
  };
  return (
    <div className="nl-gate" data-testid="newsletter-password-gate">
      <div className="nl-gate__orbs">
        <div className="nl-gate__orb nl-gate__orb--1" />
        <div className="nl-gate__orb nl-gate__orb--2" />
      </div>
      <div className="nl-gate__card">
        <div className="nl-gate__icon"><Mail size={24} /></div>
        <h2>Newsletter Dashboard</h2>
        <p>Enter admin password to manage newsletters</p>
        <form onSubmit={handleSubmit}>
          <input
            type="password" value={pw} onChange={e => setPw(e.target.value)}
            placeholder="••••••••" className={error ? 'nl-gate__input--error' : ''}
            autoFocus data-testid="newsletter-password-input"
          />
          <button type="submit" data-testid="newsletter-password-submit">
            Unlock <ArrowUpRight size={15} />
          </button>
        </form>
      </div>
    </div>
  );
}

/* ====== Main Dashboard ====== */
export default function AdminNewsletter() {
  const [password, setPassword] = useState(null);
  const [tab, setTab] = useState('compose');
  const [subscribers, setSubscribers] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);

  // Compose state
  const [prompt, setPrompt] = useState('');
  const [generatedDraft, setGeneratedDraft] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [sending, setSending] = useState(false);
  const [sendResult, setSendResult] = useState(null);

  // Preview state
  const [previewHtml, setPreviewHtml] = useState(null);
  const [previewId, setPreviewId] = useState(null);

  const fetchSubscribers = useCallback(async () => {
    if (!password) return;
    try {
      const res = await fetch(`${API}/api/newsletter/subscribers?password=${password}`);
      if (res.status === 401) { setPassword(null); return; }
      const data = await res.json();
      setSubscribers(data);
    } catch (e) { if (process.env.NODE_ENV === 'development') console.error('Fetch subscribers error:', e); }
  }, [password]);

  const fetchHistory = useCallback(async () => {
    if (!password) return;
    try {
      const res = await fetch(`${API}/api/newsletter/history?password=${password}`);
      if (res.status === 401) { setPassword(null); return; }
      const data = await res.json();
      setHistory(data.newsletters || []);
    } catch (e) { if (process.env.NODE_ENV === 'development') console.error('Fetch history error:', e); }
  }, [password]);

  useEffect(() => {
    if (password) {
      fetchSubscribers();
      fetchHistory();
    }
  }, [password, fetchSubscribers, fetchHistory]);

  const handleGenerate = async () => {
    setGenerating(true);
    setGeneratedDraft(null);
    setSendResult(null);
    try {
      const res = await fetch(`${API}/api/newsletter/generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password, prompt: prompt || undefined }),
      });
      if (!res.ok) throw new Error('Generation failed');
      const data = await res.json();
      setGeneratedDraft(data);
    } catch (e) {
      if (process.env.NODE_ENV === 'development') console.error('Generate error:', e);
      alert('Failed to generate newsletter.');
    } finally {
      setGenerating(false);
    }
  };

  const handleSend = async (newsletterId) => {
    if (!window.confirm(`Send this newsletter to all ${subscribers?.active_count || 0} active subscribers?`)) return;
    setSending(true);
    setSendResult(null);
    try {
      const res = await fetch(`${API}/api/newsletter/send`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password, newsletter_id: newsletterId }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Send failed');
      setSendResult(data);
      fetchHistory();
    } catch (e) {
      if (process.env.NODE_ENV === 'development') console.error('Send error:', e);
      setSendResult({ status: 'error', message: e.message });
    } finally {
      setSending(false);
    }
  };

  const handleDelete = async (newsletterId) => {
    if (!window.confirm('Delete this draft?')) return;
    try {
      await fetch(`${API}/api/newsletter/draft/${newsletterId}?password=${password}`, { method: 'DELETE' });
      setGeneratedDraft(null);
      fetchHistory();
    } catch (e) { if (process.env.NODE_ENV === 'development') console.error('Delete error:', e); }
  };

  const handlePreview = async (newsletterId) => {
    try {
      const res = await fetch(`${API}/api/newsletter/preview/${newsletterId}?password=${password}`);
      const data = await res.json();
      setPreviewHtml(data.html_content);
      setPreviewId(newsletterId);
    } catch (e) { if (process.env.NODE_ENV === 'development') console.error('Preview error:', e); }
  };

  if (!password) return <PasswordGate onUnlock={setPassword} />;

  return (
    <div className="nl-dash" data-testid="newsletter-dashboard">
      {/* Sidebar */}
      <aside className="nl-sidebar">
        <div className="nl-sidebar__brand">
          <Mail size={20} />
          <span>Newsletter</span>
        </div>
        <nav className="nl-sidebar__nav">
          {[
            { id: 'compose', icon: Plus, label: 'Compose' },
            { id: 'subscribers', icon: Users, label: 'Subscribers' },
            { id: 'history', icon: Clock, label: 'History' },
          ].map(item => (
            <button
              key={item.id}
              className={`nl-sidebar__item ${tab === item.id ? 'nl-sidebar__item--active' : ''}`}
              onClick={() => { setTab(item.id); setPreviewHtml(null); }}
              data-testid={`nl-tab-${item.id}`}
            >
              <item.icon size={17} />
              <span>{item.label}</span>
            </button>
          ))}
        </nav>
        <a href="/admin/analytics" className="nl-sidebar__back">← Analytics</a>
      </aside>

      {/* Main Content */}
      <main className="nl-main">
        {/* Header */}
        <header className="nl-header">
          <div>
            <h1 className="nl-header__title">
              {tab === 'compose' && 'Compose Newsletter'}
              {tab === 'subscribers' && 'Subscribers'}
              {tab === 'history' && 'Send History'}
            </h1>
            <p className="nl-header__sub">
              {tab === 'compose' && 'Generate AI-powered newsletters and send to your audience'}
              {tab === 'subscribers' && `${subscribers?.active_count || 0} active subscribers`}
              {tab === 'history' && `${history.length} newsletters sent`}
            </p>
          </div>
          <button className="nl-header__refresh" onClick={() => { fetchSubscribers(); fetchHistory(); }}>
            <RefreshCw size={16} /> Refresh
          </button>
        </header>

        {/* COMPOSE TAB */}
        {tab === 'compose' && (
          <div className="nl-compose" data-testid="nl-compose-section">
            {/* Prompt Input */}
            <div className="nl-card">
              <div className="nl-card__header">
                <Sparkles size={18} />
                <h3>AI Newsletter Generator</h3>
              </div>
              <div className="nl-card__body">
                <label className="nl-label">Topic / Custom Prompt <span>(optional)</span></label>
                <textarea
                  className="nl-textarea"
                  rows={4}
                  placeholder="e.g. 'Write about AI chatbots for dental offices' or leave blank for a general AI tips newsletter..."
                  value={prompt}
                  onChange={e => setPrompt(e.target.value)}
                  data-testid="nl-prompt-input"
                />
                <button
                  className="nl-btn nl-btn--primary"
                  onClick={handleGenerate}
                  disabled={generating}
                  data-testid="nl-generate-btn"
                >
                  {generating ? (
                    <><RefreshCw size={16} className="nl-spin" /> Generating with AI...</>
                  ) : (
                    <><Sparkles size={16} /> Generate Newsletter</>
                  )}
                </button>
              </div>
            </div>

            {/* Generated Draft Preview */}
            {generatedDraft && (
              <div className="nl-card nl-card--draft" data-testid="nl-draft-preview">
                <div className="nl-card__header">
                  <FileText size={18} />
                  <h3>Draft Preview</h3>
                  <span className="nl-badge nl-badge--draft">Draft</span>
                </div>
                <div className="nl-card__body">
                  <div className="nl-draft__subject">
                    <span className="nl-draft__label">Subject</span>
                    <p data-testid="nl-draft-subject">{generatedDraft.subject}</p>
                  </div>
                  <div className="nl-draft__sections">
                    {generatedDraft.sections?.map((section) => (
                      <div key={section.title} className="nl-draft__section">
                        <h4>{section.title}</h4>
                        {section.content.split('\n\n').map((p, j) => (
                          <p key={`${section.title}-p-${j}`}>{p}</p>
                        ))}
                      </div>
                    ))}
                  </div>
                  <div className="nl-draft__cta-note">
                    <AlertCircle size={14} />
                    <span>Each email includes a CTA linking to quiz.skyeai.io and an unsubscribe link</span>
                  </div>
                  <div className="nl-draft__actions">
                    <button
                      className="nl-btn nl-btn--primary"
                      onClick={() => handleSend(generatedDraft.newsletter_id)}
                      disabled={sending || !subscribers?.active_count}
                      data-testid="nl-send-btn"
                    >
                      {sending ? (
                        <><RefreshCw size={16} className="nl-spin" /> Sending...</>
                      ) : (
                        <><Send size={16} /> Send to {subscribers?.active_count || 0} Subscribers</>
                      )}
                    </button>
                    <button
                      className="nl-btn nl-btn--ghost"
                      onClick={() => handlePreview(generatedDraft.newsletter_id)}
                      data-testid="nl-preview-btn"
                    >
                      <Eye size={16} /> HTML Preview
                    </button>
                    <button
                      className="nl-btn nl-btn--danger"
                      onClick={() => handleDelete(generatedDraft.newsletter_id)}
                    >
                      <Trash2 size={16} /> Discard
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Send Result */}
            {sendResult && (
              <div className={`nl-result ${sendResult.status === 'error' ? 'nl-result--error' : 'nl-result--success'}`}
                   data-testid="nl-send-result">
                {sendResult.status === 'sent' ? (
                  <>
                    <CheckCircle size={20} />
                    <div>
                      <strong>Newsletter sent!</strong>
                      <p>Delivered to {sendResult.sent_count} subscriber{sendResult.sent_count !== 1 ? 's' : ''}.
                        {sendResult.failed_count > 0 && ` ${sendResult.failed_count} failed.`}
                      </p>
                    </div>
                  </>
                ) : (
                  <>
                    <XCircle size={20} />
                    <div>
                      <strong>Send failed</strong>
                      <p>{sendResult.message || 'An error occurred'}</p>
                    </div>
                  </>
                )}
              </div>
            )}

            {/* HTML Preview Modal */}
            {previewHtml && (
              <div className="nl-preview-overlay" onClick={() => setPreviewHtml(null)}>
                <div className="nl-preview-modal" onClick={e => e.stopPropagation()}>
                  <div className="nl-preview-modal__header">
                    <h3>Email Preview</h3>
                    <button onClick={() => setPreviewHtml(null)}>&times;</button>
                  </div>
                  <iframe
                    srcDoc={previewHtml}
                    title="Newsletter Preview"
                    className="nl-preview-iframe"
                    data-testid="nl-preview-iframe"
                  />
                </div>
              </div>
            )}
          </div>
        )}

        {/* SUBSCRIBERS TAB */}
        {tab === 'subscribers' && (
          <div className="nl-subscribers" data-testid="nl-subscribers-section">
            {/* Stats Row */}
            <div className="nl-stats-row">
              <div className="nl-stat-card">
                <Users size={20} />
                <div>
                  <span className="nl-stat-card__num">{subscribers?.total || 0}</span>
                  <span className="nl-stat-card__label">Total</span>
                </div>
              </div>
              <div className="nl-stat-card nl-stat-card--green">
                <CheckCircle size={20} />
                <div>
                  <span className="nl-stat-card__num">{subscribers?.active_count || 0}</span>
                  <span className="nl-stat-card__label">Active</span>
                </div>
              </div>
              <div className="nl-stat-card nl-stat-card--red">
                <XCircle size={20} />
                <div>
                  <span className="nl-stat-card__num">{subscribers?.inactive_count || 0}</span>
                  <span className="nl-stat-card__label">Unsubscribed</span>
                </div>
              </div>
            </div>

            {/* Subscriber List */}
            <div className="nl-card">
              <div className="nl-card__header">
                <Users size={18} />
                <h3>All Subscribers</h3>
              </div>
              <div className="nl-table-wrap">
                {subscribers?.subscribers?.length > 0 ? (
                  <table className="nl-table" data-testid="nl-subscribers-table">
                    <thead>
                      <tr>
                        <th>Email</th>
                        <th>Status</th>
                        <th>Subscribed</th>
                      </tr>
                    </thead>
                    <tbody>
                      {subscribers.subscribers.map((sub) => (
                        <tr key={sub.email}>
                          <td className="nl-table__email">{sub.email}</td>
                          <td>
                            <span className={`nl-badge ${sub.active ? 'nl-badge--active' : 'nl-badge--inactive'}`}>
                              {sub.active ? 'Active' : 'Unsubscribed'}
                            </span>
                          </td>
                          <td className="nl-table__date">
                            {new Date(sub.subscribed_at).toLocaleDateString()}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <div className="nl-empty">
                    <Mail size={32} />
                    <p>No subscribers yet</p>
                    <span>Subscribers will appear here once people sign up via the footer</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* HISTORY TAB */}
        {tab === 'history' && (
          <div className="nl-history" data-testid="nl-history-section">
            <div className="nl-card">
              <div className="nl-card__header">
                <Clock size={18} />
                <h3>Newsletter History</h3>
              </div>
              <div className="nl-card__body">
                {history.length > 0 ? (
                  <div className="nl-history-list">
                    {history.map((item) => (
                      <div key={item._id || item.subject} className="nl-history-item" data-testid={`nl-history-item-${item._id || item.subject}`}>
                        <div className="nl-history-item__info">
                          <h4>{item.subject}</h4>
                          <div className="nl-history-item__meta">
                            <span className={`nl-badge ${item.status === 'sent' ? 'nl-badge--sent' : 'nl-badge--draft'}`}>
                              {item.status}
                            </span>
                            <span><Calendar size={13} /> {new Date(item.created_at).toLocaleDateString()}</span>
                            {item.recipient_count != null && (
                              <span><Users size={13} /> {item.recipient_count} sent</span>
                            )}
                          </div>
                          {item.prompt_used && (
                            <p className="nl-history-item__prompt">Prompt: "{item.prompt_used}"</p>
                          )}
                        </div>
                        <div className="nl-history-item__actions">
                          <button
                            className="nl-btn nl-btn--small"
                            onClick={() => handlePreview(item.newsletter_id)}
                          >
                            <Eye size={14} /> View
                          </button>
                          {item.status === 'draft' && (
                            <>
                              <button
                                className="nl-btn nl-btn--small nl-btn--primary"
                                onClick={() => handleSend(item.newsletter_id)}
                                disabled={sending}
                              >
                                <Send size={14} /> Send
                              </button>
                              <button
                                className="nl-btn nl-btn--small nl-btn--danger"
                                onClick={() => handleDelete(item.newsletter_id)}
                              >
                                <Trash2 size={14} />
                              </button>
                            </>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="nl-empty">
                    <FileText size={32} />
                    <p>No newsletters yet</p>
                    <span>Generate and send your first newsletter from the Compose tab</span>
                  </div>
                )}
              </div>
            </div>

            {/* HTML Preview Modal */}
            {previewHtml && (
              <div className="nl-preview-overlay" onClick={() => setPreviewHtml(null)}>
                <div className="nl-preview-modal" onClick={e => e.stopPropagation()}>
                  <div className="nl-preview-modal__header">
                    <h3>Email Preview</h3>
                    <button onClick={() => setPreviewHtml(null)}>&times;</button>
                  </div>
                  <iframe
                    srcDoc={previewHtml}
                    title="Newsletter Preview"
                    className="nl-preview-iframe"
                    data-testid="nl-preview-iframe"
                  />
                </div>
              </div>
            )}
          </div>
        )}
      </main>
    </div>
  );
}
